/**
 * Formation slot offsets in metres (east, north), matching the Rust
 * `crates/swarm/src/formation.rs` reference implementation.
 * Verified test cases live in the Rust crate; this TS copy is a UI mirror.
 */

export type Formation =
  | 'line'
  | 'column'
  | 'v'
  | 'triangle'
  | 'circle'
  | 'echelon'
  | 'diamond'
  | 'trishul';

export const FORMATIONS: Formation[] = [
  'line', 'column', 'v', 'triangle', 'circle', 'echelon', 'diamond', 'trishul',
];

/** Compute (east_m, north_m) slot offset before rotation. */
export function slotOffsetM(
  formation: Formation,
  slotIndex: number,
  n: number,
  spacingM: number,
): [number, number] {
  switch (formation) {
    case 'line': return line(slotIndex, n, spacingM);
    case 'column': return column(slotIndex, n, spacingM);
    case 'v': return vform(slotIndex, spacingM);
    case 'triangle': return triangle(slotIndex, spacingM);
    case 'circle': return circle(slotIndex, n, spacingM);
    case 'echelon': return echelon(slotIndex, spacingM);
    case 'diamond': return diamond(slotIndex, spacingM);
    case 'trishul': return trishul(slotIndex, spacingM);
  }
}

export function centerSlot(formation: Formation, n: number): number {
  if (formation === 'line' || formation === 'column') {
    return Math.max(0, Math.floor((n - 1) / 2));
  }
  return 0;
}

function line(i: number, n: number, spacing: number): [number, number] {
  const center = (n - 1) / 2;
  return [(i - center) * spacing, 0];
}

function column(i: number, n: number, spacing: number): [number, number] {
  const center = (n - 1) / 2;
  return [0, (i - center) * spacing];
}

function vform(i: number, spacing: number): [number, number] {
  if (i === 0) return [0, 0];
  const row = Math.ceil(i / 2);
  const side = i % 2 === 1 ? -1 : 1;
  return [side * row * spacing, -row * spacing];
}

function triangle(i: number, spacing: number): [number, number] {
  if (i === 0) return [0, 0];
  const row = Math.floor((1 + Math.sqrt(1 + 8 * i)) / 2);
  const rowStart = (row * (row - 1)) / 2;
  const col = i - rowStart - 1;
  const rowWidth = row * spacing;
  return [col * spacing - rowWidth / 2, -row * spacing];
}

function circle(i: number, n: number, spacing: number): [number, number] {
  if (n === 0) return [0, 0];
  const radius = (spacing * n) / (2 * Math.PI);
  const theta = (2 * Math.PI * i) / n;
  return [radius * Math.cos(theta), radius * Math.sin(theta)];
}

function echelon(i: number, spacing: number): [number, number] {
  return [i * spacing, -i * spacing];
}

function diamond(i: number, spacing: number): [number, number] {
  switch (i) {
    case 0: return [0, 0];
    case 1: return [spacing, 0];
    case 2: return [-spacing, 0];
    case 3: return [0, spacing];
    case 4: return [0, -spacing];
    default: {
      const row = Math.ceil(i - 4);
      return [0, -row * spacing];
    }
  }
}

function trishul(i: number, spacing: number): [number, number] {
  switch (i) {
    case 0: return [0, 0];
    case 1: return [-spacing * 2, 0];
    case 2: return [spacing * 2, 0];
    case 3: return [-spacing, -spacing];
    case 4: return [spacing, -spacing];
    default: return [0, -i * spacing];
  }
}
